/**
 * Microphone capture.
 *
 * Three iOS-specific rules are baked in here, all verified against WebKit
 * behaviour in research/06. Getting any of them wrong produces a feature that
 * works on desktop and silently fails on the phone the user actually owns:
 *
 * 1. getUserMedia() must be called BEFORE constructing the AudioContext.
 *    This is the reverse of the usual desktop habit.
 * 2. iOS has a non-standard 'interrupted' AudioContext state (a phone call,
 *    Siri, another app taking audio). Guards that only check for 'suspended'
 *    are dead code on iOS.
 * 3. noiseSuppression and autoGainControl are undeclared in WebKit and
 *    silently ignored — AGC cannot be turned off. So nothing downstream may
 *    depend on absolute amplitude; shimmer is computed as a relative measure
 *    and the voiced-frame floor is relative to the recording's own peak.
 *
 * Requires a secure context (https:// or localhost).
 */

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'processing' | 'error';

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private chunks: Float32Array[] = [];

  sampleRate = 48000;

  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      (typeof AudioContext !== 'undefined' ||
        typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !==
          'undefined')
    );
  }

  static unsupportedReason(): string {
    if (typeof navigator === 'undefined') return 'No browser environment.';
    if (!window.isSecureContext) {
      return 'The microphone needs a secure connection (https). This page is not on one.';
    }
    if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
      return 'This browser does not provide microphone access. On iOS, Lockdown Mode disables it.';
    }
    return 'Audio recording is unavailable here.';
  }

  async start(): Promise<void> {
    // Rule 1: microphone permission first, AudioContext second.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        // Declared for browsers that honour them. WebKit ignores both; that is
        // why nothing downstream relies on absolute level.
        noiseSuppression: false,
        autoGainControl: false,
      },
      video: false,
    });

    const Ctor =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

    this.context = new Ctor();
    this.sampleRate = this.context.sampleRate;

    // Rule 2: iOS may hand back a context that is suspended OR interrupted.
    if (this.context.state !== 'running') {
      await this.context.resume();
    }

    this.source = this.context.createMediaStreamSource(this.stream);

    // ScriptProcessor is deprecated but is the only node available in every
    // browser we target without shipping a separate AudioWorklet module file.
    // At 4096 samples the callback rate is ~11 Hz, which is comfortable.
    this.processor = this.context.createScriptProcessor(4096, 1, 1);
    this.chunks = [];

    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      this.chunks.push(new Float32Array(input));
    };

    this.source.connect(this.processor);
    // The processor must reach a destination for onaudioprocess to fire, but we
    // don't want to play the microphone back — route through a muted gain node.
    const mute = this.context.createGain();
    mute.gain.value = 0;
    this.processor.connect(mute);
    mute.connect(this.context.destination);
  }

  /** Seconds captured so far. */
  get elapsed(): number {
    const samples = this.chunks.reduce((n, c) => n + c.length, 0);
    return samples / this.sampleRate;
  }

  async stop(): Promise<{ samples: Float32Array; sampleRate: number }> {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    await this.context?.close();

    const total = this.chunks.reduce((n, c) => n + c.length, 0);
    const samples = new Float32Array(total);
    let offset = 0;
    for (const chunk of this.chunks) {
      samples.set(chunk, offset);
      offset += chunk.length;
    }

    const sampleRate = this.sampleRate;
    this.stream = null;
    this.context = null;
    this.source = null;
    this.processor = null;
    this.chunks = [];

    return { samples, sampleRate };
  }

  cancel(): void {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.context?.close();
    this.chunks = [];
  }
}
